import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, ReactNode } from 'react';

import { HardSkillIcon } from '../../core/icons/HardSkillIcon';
import { LanguageSkillIcon } from '../../core/icons/LanguageSkillIcon';
import { SoftSkillIcon } from '../../core/icons/SoftSkillIcon';

export type SkillBadgeProps = HTMLAttributes<HTMLDivElement> & {
    readonly actions?: ReactNode;
    readonly skill: {
        readonly title: string;
        readonly type: 'hard' | 'language' | 'soft';
    };
};

const skillBadgeClasses = clsx(
    'flex items-center justify-center',
    'gap-s py-s px-l',
    'bg-gray-200 text-black-900 rounded-md shadow-md',
    'text-sm font-medium leading-[1.25rem] tracking-[-0.0175rem] font-sans',
    'select-none cursor-default overflow-hidden antialiased',
    'transition-[background-color,color,box-shadow,opacity] duration-200 ease-in-out',
    '[&>svg]:shrink-0',
    'hover:bg-gray-100 hover:shadow-lg hover:cursor-pointer',
    'focus-visible:outline focus-visible:outline-1 focus-visible:outline-orange-800',
);

export const SkillBadge: FunctionComponent<SkillBadgeProps> = ({ actions, className, skill, ...props }) => {
    return (
        // biome-ignore lint/a11y/useSemanticElements: suppressed due to migration
        <div
            className={clsx(skillBadgeClasses, className)}
            role="button"
            {...props}
            tabIndex={props.onClick !== undefined ? 0 : undefined}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    props.onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
                }
            }}
        >
            {skill.type === 'hard' ? <HardSkillIcon /> : null}
            {skill.type === 'language' ? <LanguageSkillIcon /> : null}
            {skill.type === 'soft' ? <SoftSkillIcon /> : null}
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{skill.title}</span>
            {actions}
        </div>
    );
};
